import { ApiClient } from '../config';

const apiClient = new ApiClient();

export interface AuditLogEntry {
  audit_id: string;
  event_type: string;
  resource_type: string;
  resource_id?: string;
  user_id: string;
  user_email: string;
  user_role: string;
  session_id?: string;
  ip_address?: string;
  user_agent?: string;
  request_id?: string;
  api_endpoint?: string;
  http_method?: string;
  old_values?: any;
  new_values?: any;
  changes_summary?: string;
  success: boolean;
  error_message?: string;
  metadata?: any;
  timestamp: string;
}

export interface AuditSearchFilters {
  userId?: string;
  resourceType?: string;
  resourceId?: string;
  eventType?: string;
  startDate?: string;
  endDate?: string;
  search?: string;
  limit?: number;
  offset?: number;
}

export interface AuditSearchResult {
  entries: AuditLogEntry[];
  total: number;
  hasMore: boolean;
}

export interface AuditConfig {
  resourceType: string;
  enabled: boolean;
  trackReads: boolean;
  retentionDays: number;
  sensitiveFields: string[];
}

export interface AuditStats {
  period: string;
  byResourceType: Array<{
    resource_type: string;
    total_events: number;
    unique_users: number;
    creates: number;
    updates: number;
    deletes: number;
    failures: number;
  }>;
  totalEvents: number;
}

class AuditService {
  async searchAuditLogs(filters: AuditSearchFilters): Promise<AuditSearchResult> {
    const params = new URLSearchParams();
    
    if (filters.userId) params.append('userId', filters.userId);
    if (filters.resourceType) params.append('resourceType', filters.resourceType);
    if (filters.resourceId) params.append('resourceId', filters.resourceId);
    if (filters.eventType) params.append('eventType', filters.eventType);
    if (filters.startDate) params.append('startDate', filters.startDate);
    if (filters.endDate) params.append('endDate', filters.endDate);
    if (filters.search) params.append('search', filters.search);
    if (filters.limit) params.append('limit', filters.limit.toString());
    if (filters.offset) params.append('offset', filters.offset.toString());

    const response = await apiClient.get<AuditSearchResult>(`/api/v1/audit?${params.toString()}`);
    return response || { entries: [], total: 0, hasMore: false };
  }

  async exportAuditLogs(filters: AuditSearchFilters): Promise<Blob> {
    const params = new URLSearchParams();

    if (filters.userId) params.append('userId', filters.userId);
    if (filters.resourceType) params.append('resourceType', filters.resourceType);
    if (filters.resourceId) params.append('resourceId', filters.resourceId);
    if (filters.eventType) params.append('eventType', filters.eventType);
    if (filters.startDate) params.append('startDate', filters.startDate);
    if (filters.endDate) params.append('endDate', filters.endDate);
    if (filters.search) params.append('search', filters.search);

    const response = await apiClient.get<Blob>(`/api/v1/audit/export?${params.toString()}`);

    return response;
  }

  async getAuditStats(days: number = 30): Promise<AuditStats> {
    const response = await apiClient.get<AuditStats>(`/api/v1/audit/stats?days=${days}`);
    return response;
  }

  async getAuditConfig(): Promise<AuditConfig[]> {
    const response = await apiClient.get<AuditConfig[]>('/api/v1/audit/config');
    return response;
  }

  async updateAuditConfig(resourceType: string, config: Partial<AuditConfig>): Promise<void> {
    await apiClient.put(`/api/v1/audit/config/${resourceType}`, config);
  }

  async cleanupAuditLogs(): Promise<{ deletedCount: number }> {
    const response = await apiClient.post<{ deletedCount: number }>('/api/v1/audit/cleanup', {});
    return response;
  }
}

export const auditService = new AuditService();